import { ButtonVariant, ButtonTone } from './button.types';

import styled from '@emotion/styled';
import { ButtonSize } from './button.types';
import { Theme } from '@emotion/react';
import { css } from '@emotion/react';

// Helper function to get disabled styles
const getDisabledStyles = (theme: Theme) => css`
  background: ${theme.colors.technical.gray[5]};
  color: ${theme.colors.technical.gray[15]};
  cursor: not-allowed;
`;

// Helper function to get the appropriate color set based on tone
const getToneColors = (theme: Theme, tone: ButtonTone) => {
  switch (tone) {
    case 'error':
      return {
        primary: theme.colors.status.error.normal,
        primaryHover: theme.colors.status.error.dark,
        primaryActive: theme.colors.status.error.normal,
        primaryText: theme.colors.technical.white,
        secondary: theme.colors.status.error.highlight,
        secondaryHover: theme.colors.status.error.light,
        secondaryActive: theme.colors.status.error.normal,
        secondaryText: theme.colors.status.error.dark,
      };
    case 'warning':
      return {
        primary: theme.colors.status.warning.normal,
        primaryHover: theme.colors.status.warning.dark,
        primaryActive: theme.colors.status.warning.normal,
        primaryText: theme.colors.technical.white,
        secondary: theme.colors.status.warning.highlight,
        secondaryHover: theme.colors.status.warning.light,
        secondaryActive: theme.colors.status.warning.normal,
        secondaryText: theme.colors.status.warning.dark,
      };
    case 'info':
      return {
        primary: theme.colors.status.info.normal,
        primaryHover: theme.colors.status.info.dark,
        primaryActive: theme.colors.status.info.normal,
        primaryText: theme.colors.technical.white,
        secondary: theme.colors.status.info.highlight,
        secondaryHover: theme.colors.status.info.light,
        secondaryActive: theme.colors.status.info.normal,
        secondaryText: theme.colors.status.info.dark,
      };
    case 'neutral':
      return {
        primary: theme.colors.technical.gray[15],
        primaryHover: theme.colors.technical.gray[30],
        primaryActive: theme.colors.technical.gray[60],
        primaryText: theme.colors.technical.black,
        secondary: theme.colors.technical.gray[5],
        secondaryHover: theme.colors.technical.gray[15],
        secondaryActive: theme.colors.technical.gray[30],
        secondaryText: theme.colors.technical.black,
      };
    default:
      return {
        primary: theme.colors.primary.normal,
        primaryHover: theme.colors.primary.medium,
        primaryActive: theme.colors.primary.dark,
        primaryText: theme.colors.technical.white,
        secondary: theme.colors.secondary.normal,
        secondaryHover: theme.colors.secondary.medium,
        secondaryActive: theme.colors.secondary.dark,
        secondaryText: theme.colors.primary.normal,
      };
  }
};

export const Button = styled.button<{
  size: ButtonSize;
  variant: ButtonVariant;
  tone: ButtonTone;
  hasIcon: boolean;
  isFocusVisible: boolean;
  isLoading?: boolean;
  $width?: number;
  $height?: number;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
  width: fit-content;
  position: relative;
  outline: none;

  /* Apply dimensions when loading */
  ${({ $width, $height, isLoading }) =>
    isLoading && $width && $height
      ? css`
          min-width: ${$width}px;
          min-height: ${$height}px;
        `
      : ''}

  /* Size variants */
  ${({ size, hasIcon }) => {
    if (hasIcon) {
      return {
        small: css`
          padding: 8px;
          font-size: 14px;
          line-height: 16px;
          min-height: 32px;

          .icon {
            width: 16px;
            height: 16px;
          }
        `,
        medium: css`
          padding: 12px;
          font-size: 16px;
          line-height: 24px;
          min-height: 48px;

          .icon {
            width: 20px;
            height: 20px;
          }
        `,
        large: css`
          padding: 18px;
          font-size: 18px;
          line-height: 28px;
          min-height: 64px;

          .icon {
            width: 24px;
            height: 24px;
          }
        `,
      }[size];
    }

    return {
      small: css`
        padding: 8px 16px;
        font-size: 14px;
        line-height: 16px;
        min-height: 32px;

        .icon {
          width: 16px;
          height: 16px;
        }
      `,
      medium: css`
        padding: 12px 24px;
        font-size: 16px;
        line-height: 24px;
        min-height: 48px;

        .icon {
          width: 20px;
          height: 20px;
        }
      `,
      large: css`
        padding: 18px 24px;
        font-size: 18px;
        line-height: 28px;
        min-height: 64px;

        .icon {
          width: 24px;
          height: 24px;
        }
      `,
    }[size];
  }}

  /* Color variants and tones */
  ${({ variant, tone, theme }) => {
    const colors = getToneColors(theme, tone);

    const variantStyles = {
      primary: css`
        background: ${colors.primary};
        color: ${colors.primaryText};
        &:hover {
          background: ${colors.primaryHover};
        }
        &:active {
          background: ${colors.primaryActive};
        }
        &[aria-disabled='true'],
        &[aria-busy='true'] {
          ${getDisabledStyles(theme)}
        }
      `,
      secondary: css`
        background: ${colors.secondary};
        color: ${colors.secondaryText};
        &:hover {
          background: ${colors.secondaryHover};
        }
        &:active {
          background: ${colors.secondaryActive};
        }
        &[aria-disabled='true'],
        &[aria-busy='true'] {
          ${getDisabledStyles(theme)}
        }
      `,
      text: css`
        background: transparent;
        color: ${colors.secondaryText};
        &:hover {
          background: ${colors.secondaryHover};
        }
        &:active {
          background: ${colors.secondaryActive};
        }
        &[aria-disabled='true'],
        &[aria-busy='true'] {
          ${getDisabledStyles(theme)}
        }
      `,
    };

    return variantStyles[variant];
  }}

  ${({ isLoading }) =>
    isLoading &&
    css`
      pointer-events: none;
      cursor: not-allowed;
      /* This ensures keyboard interactions are also disabled */
      &:focus {
        outline: none;
      }
    `}

  /* Focus ring */
  ${({ isFocusVisible, theme }) =>
    isFocusVisible &&
    css`
      &:after {
        content: '';
        position: absolute;
        inset: -5px;
        border-radius: 11px;
        border: 2px solid ${theme.colors.primary.normal};
      }
    `}

  ${({ hasIcon, size }) =>
    hasIcon && {
      padding: {
        small: '8px',
        medium: '12px',
        large: '16px',
      }[size],
      aspectRatio: '1',
    }}
`;

export const ContentWrapper = styled.div<{ isHidden?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  visibility: ${({ isHidden }) => (isHidden ? 'hidden' : 'visible')};
`;

export const LoadingWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
